import { useEffect, useRef, useCallback } from 'react';
import { DeviceInfo, VideoJSPlayer } from '../types';

interface TouchGesturesConfig {
  onSeekForward?: () => void;
  onSeekBackward?: () => void;
  onTogglePlay?: () => void;
  onShowControls?: () => void;
  doubleTapDelay?: number;
  enabled?: boolean;
}

const DEFAULT_CONFIG: TouchGesturesConfig = {
  doubleTapDelay: 300,
  enabled: true
};

// タッチ操作フィードバック用インジケーターのアイコン。
// 絵文字（▶/⏸/⏭/⏮）はOS/ブラウザによってフォント・色・大きさがまちまちで、
// プレイヤーの「大きな再生/一時停止ボタン」(FaPlayCircle/FaPauseCircle) と
// 見た目が揃わず別物のボタンが重なって見える（Issue #47）。SVGアイコンに統一する。
const ICON_PLAY =
  '<svg width="24" height="24" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg"><path d="M8 5v14l11-7z"/></svg>';
const ICON_PAUSE =
  '<svg width="24" height="24" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>';
const ICON_SEEK_FORWARD =
  '<svg width="24" height="24" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg"><path d="M4 5v14l9-7z"/><path d="M13 5v14l9-7z"/></svg>';
const ICON_SEEK_BACKWARD =
  '<svg width="24" height="24" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg"><path d="M20 5v14l-9-7z"/><path d="M11 5v14l-9-7z"/></svg>';

export const useTouchGestures = (
  playerRef: React.RefObject<VideoJSPlayer | null>,
  deviceInfo: DeviceInfo,
  config: TouchGesturesConfig = {}
) => {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };
  
  const lastTapRef = useRef<{ time: number; x: number; y: number } | null>(null);
  const tapTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const seekIndicatorTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // タップ効果を表示する要素の参照
  const seekIndicatorRef = useRef<HTMLDivElement | null>(null);

  // シーク方向を判定する関数
  const getSeekDirection = useCallback((x: number, containerWidth: number): 'forward' | 'backward' | 'center' => {
    const leftThreshold = containerWidth * 0.35;
    const rightThreshold = containerWidth * 0.65;
    
    if (x < leftThreshold) {
      return 'backward';
    } else if (x > rightThreshold) {
      return 'forward';
    } else {
      return 'center';
    }
  }, []);

  // シークインジケーターを表示する関数
  const showSeekIndicator = useCallback((direction: 'forward' | 'backward', x: number, y: number) => {
    if (!playerRef.current) return;

    const playerElement = playerRef.current.el();
    if (!playerElement) return;

    // 親コンポーネントのコントロール表示機能を呼び出す
    if (finalConfig.onShowControls) {
      finalConfig.onShowControls();
    }

    // 既存のインジケーターを削除
    const existingIndicator = playerElement.querySelector('.seek-indicator');
    if (existingIndicator) {
      existingIndicator.remove();
    }

    // 新しいインジケーターを作成
    const indicator = document.createElement('div');
    indicator.className = `seek-indicator seek-indicator--${direction}`;
    indicator.innerHTML = `
      <div class="seek-indicator__icon">
        ${direction === 'forward' ? ICON_SEEK_FORWARD : ICON_SEEK_BACKWARD}
      </div>
      <div class="seek-indicator__text">10秒${direction === 'forward' ? '送り' : '戻し'}</div>
    `;

    // スタイルを設定
    Object.assign(indicator.style, {
      position: 'absolute',
      top: '50%',
      left: direction === 'forward' ? '70%' : '30%',
      transform: 'translate(-50%, -50%)',
      background: 'rgba(0, 0, 0, 0.8)',
      color: 'white',
      padding: '12px 16px',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: 'bold',
      textAlign: 'center',
      zIndex: '1000',
      pointerEvents: 'none',
      animation: 'seekIndicatorFadeIn 0.3s ease-out',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '4px'
    });

    playerElement.appendChild(indicator);
    seekIndicatorRef.current = indicator;

    // 1.5秒後に削除
    if (seekIndicatorTimeoutRef.current) {
      clearTimeout(seekIndicatorTimeoutRef.current);
    }
    
    seekIndicatorTimeoutRef.current = setTimeout(() => {
      if (indicator && indicator.parentNode) {
        indicator.style.animation = 'seekIndicatorFadeOut 0.3s ease-out';
        setTimeout(() => {
          if (indicator.parentNode) {
            indicator.parentNode.removeChild(indicator);
          }
        }, 300);
      }
      seekIndicatorRef.current = null;
      
      // シークインジケーターが消えた後、再度コントロール表示タイマーを開始
      if (finalConfig.onShowControls) {
        finalConfig.onShowControls();
      }
    }, 1500);
  }, [playerRef, finalConfig]);

  // 再生/一時停止インジケーターを表示する関数
  const showPlayPauseIndicator = useCallback((isPlaying: boolean, x: number, y: number) => {
    if (!playerRef.current) return;

    const playerElement = playerRef.current.el();
    if (!playerElement) return;

    // 親コンポーネントのコントロール表示機能を呼び出す
    if (finalConfig.onShowControls) {
      finalConfig.onShowControls();
    }

    // 既存のインジケーターを削除
    const existingIndicator = playerElement.querySelector('.play-pause-indicator');
    if (existingIndicator) {
      existingIndicator.remove();
    }

    // 新しいインジケーターを作成
    const indicator = document.createElement('div');
    indicator.className = 'play-pause-indicator';
    indicator.innerHTML = `
      <div class="play-pause-indicator__icon">
        ${isPlaying ? ICON_PAUSE : ICON_PLAY}
      </div>
      <div class="play-pause-indicator__text">${isPlaying ? '一時停止' : '再生'}</div>
    `;

    // スタイルを設定
    Object.assign(indicator.style, {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      background: 'rgba(0, 0, 0, 0.8)',
      color: 'white',
      padding: '12px 16px',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: 'bold',
      textAlign: 'center',
      zIndex: '1000',
      pointerEvents: 'none',
      animation: 'playPauseIndicatorFadeIn 0.3s ease-out',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '4px'
    });

    playerElement.appendChild(indicator);

    // 1秒後に削除
    setTimeout(() => {
      if (indicator && indicator.parentNode) {
        indicator.style.animation = 'playPauseIndicatorFadeOut 0.3s ease-out';
        setTimeout(() => {
          if (indicator.parentNode) {
            indicator.parentNode.removeChild(indicator);
          }
        }, 300);
      }
      
      // インジケーターが消えた後、再度コントロール表示タイマーを開始
      if (finalConfig.onShowControls) {
        finalConfig.onShowControls();
      }
    }, 1000);
  }, [playerRef, finalConfig]);

  // タッチイベントハンドラー
  const handleTouchEnd = useCallback((event: Event) => {
    const touchEvent = event as TouchEvent;
    if (!finalConfig.enabled || !deviceInfo.isMobile || !playerRef.current) return;

    const touch = touchEvent.changedTouches[0];
    if (!touch) return;

    const playerElement = playerRef.current.el();
    if (!playerElement) return;

    // プレイヤー要素内のタッチかチェック
    const rect = playerElement.getBoundingClientRect();
    const isInsidePlayer = 
      touch.clientX >= rect.left &&
      touch.clientX <= rect.right &&
      touch.clientY >= rect.top &&
      touch.clientY <= rect.bottom;

    if (!isInsidePlayer) return;

    const now = Date.now();
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;

    // ダブルタップの判定
    if (lastTapRef.current) {
      const timeDiff = now - lastTapRef.current.time;
      const xDiff = Math.abs(x - lastTapRef.current.x);
      const yDiff = Math.abs(y - lastTapRef.current.y);
      
      // ダブルタップの条件: 時間間隔が指定値以内、位置が近い
      if (timeDiff < finalConfig.doubleTapDelay! && xDiff < 50 && yDiff < 50) {
        // ダブルタップ処理: 10秒送り/戻り
        const direction = getSeekDirection(x, rect.width);
        
        if (direction === 'forward' && finalConfig.onSeekForward) {
          finalConfig.onSeekForward();
          showSeekIndicator('forward', x, y);
          console.log('ダブルタップ: 10秒送り');
        } else if (direction === 'backward' && finalConfig.onSeekBackward) {
          finalConfig.onSeekBackward();
          showSeekIndicator('backward', x, y);
          console.log('ダブルタップ: 10秒戻し');
        }
        
        // ダブルタップ完了後、情報をクリア
        lastTapRef.current = null;
        if (tapTimeoutRef.current) {
          clearTimeout(tapTimeoutRef.current);
          tapTimeoutRef.current = null;
        }
        return;
      }
    }

    // シングルタップの情報を記録
    lastTapRef.current = { time: now, x, y };
    
    // タイムアウト後にシングルタップ処理を実行
    if (tapTimeoutRef.current) {
      clearTimeout(tapTimeoutRef.current);
    }
    
    tapTimeoutRef.current = setTimeout(() => {
      // シングルタップ処理: 再生/一時停止
      if (finalConfig.onTogglePlay) {
        // 現在の再生状態を取得してインジケーターを表示
        const isCurrentlyPlaying = !playerRef.current?.paused();
        finalConfig.onTogglePlay();
        showPlayPauseIndicator(!isCurrentlyPlaying, x, y);
        console.log('シングルタップ: 再生/一時停止');
      }
      
      lastTapRef.current = null;
      tapTimeoutRef.current = null;
    }, finalConfig.doubleTapDelay!);
  }, [
    finalConfig,
    deviceInfo.isMobile,
    playerRef,
    getSeekDirection,
    showSeekIndicator,
    showPlayPauseIndicator
  ]);

  // イベントリスナーの設定
  useEffect(() => {
    if (!finalConfig.enabled || !deviceInfo.isMobile || !playerRef.current) return;

    const playerElement = playerRef.current.el();
    if (!playerElement) return;

    // パッシブリスナーでタッチイベントを登録
    playerElement.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      playerElement.removeEventListener('touchend', handleTouchEnd);
      
      // クリーンアップ
      if (tapTimeoutRef.current) {
        clearTimeout(tapTimeoutRef.current);
      }
      if (seekIndicatorTimeoutRef.current) {
        clearTimeout(seekIndicatorTimeoutRef.current);
      }
      if (seekIndicatorRef.current && seekIndicatorRef.current.parentNode) {
        seekIndicatorRef.current.parentNode.removeChild(seekIndicatorRef.current);
      }
    };
  }, [finalConfig.enabled, deviceInfo.isMobile, playerRef, handleTouchEnd]);

  // CSSアニメーションを動的に追加
  useEffect(() => {
    if (!deviceInfo.isMobile) return;

    const styleId = 'touch-gesture-animations';
    const existingStyle = document.getElementById(styleId);
    
    if (existingStyle) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @keyframes seekIndicatorFadeIn {
        from {
          opacity: 0;
          transform: translate(-50%, -50%) scale(0.8);
        }
        to {
          opacity: 1;
          transform: translate(-50%, -50%) scale(1);
        }
      }
      
      @keyframes seekIndicatorFadeOut {
        from {
          opacity: 1;
          transform: translate(-50%, -50%) scale(1);
        }
        to {
          opacity: 0;
          transform: translate(-50%, -50%) scale(0.8);
        }
      }
      
      @keyframes playPauseIndicatorFadeIn {
        from {
          opacity: 0;
          transform: translate(-50%, -50%) scale(0.8);
        }
        to {
          opacity: 1;
          transform: translate(-50%, -50%) scale(1);
        }
      }
      
      @keyframes playPauseIndicatorFadeOut {
        from {
          opacity: 1;
          transform: translate(-50%, -50%) scale(1);
        }
        to {
          opacity: 0;
          transform: translate(-50%, -50%) scale(0.8);
        }
      }
      
      .seek-indicator__icon {
        font-size: 24px;
        margin-bottom: 4px;
      }
      
      .seek-indicator__text {
        font-size: 12px;
        opacity: 0.9;
      }
      
      .play-pause-indicator__icon {
        font-size: 24px;
        margin-bottom: 4px;
      }
      
      .play-pause-indicator__text {
        font-size: 12px;
        opacity: 0.9;
      }
    `;
    
    document.head.appendChild(style);
    
    return () => {
      const styleElement = document.getElementById(styleId);
      if (styleElement) {
        styleElement.remove();
      }
    };
  }, [deviceInfo.isMobile]);

  return {
    // 設定の状態
    isEnabled: finalConfig.enabled && deviceInfo.isMobile,
    
    // 制御関数
    setEnabled: (enabled: boolean) => {
      finalConfig.enabled = enabled;
    }
  };
}; 